/**
 * @module UseContainerTriageSubmit
 * @purpose Owns the selective-run submit call: `startIngestionRun` with
 *   `selectedItemKeys`/`reingestItemKeys`, mapping `ENTRY_ALREADY_INGESTED`
 *   and `RUN_ALREADY_ACTIVE` to user-facing state via `mapSubmitError`. Also
 *   owns the started run's inline progress handoff: the creation response
 *   carries an `operationId` (evidence-container-foundation PRP addendum,
 *   best-effort -- `null` when the progress row itself failed to write) that
 *   the caller hands to the shared OperationProgress facility until it is
 *   acknowledged as complete.
 * @depends services/ingestion (startIngestionRun), container-triage.helpers
 * @dependedOnBy use-container-triage
 *
 * @rules
 * - State: local useState; `submitting` -> `submittedRun` | `error`+`errorCode`
 * - Errors: server-generated messages (both mapped codes ship humanised text)
 *   are surfaced verbatim; anything else falls back to a translated key at
 *   the render layer
 */

import { useCallback, useState } from 'react'
import { startIngestionRun } from '../../../services/ingestion'
import type { IngestionRunStart } from '../../../types/ingestion'
import { mapSubmitError } from './container-triage.helpers'

export interface SubmitTriageArgs {
  caseId: string
  receiptId: string
  selectedKeys: ReadonlySet<string>
  reingestKeys: ReadonlySet<string>
}

export interface UseContainerTriageSubmitResult {
  submitting: boolean
  submittedRun: IngestionRunStart | null
  submittedOperationId: string | null
  error: string | null
  errorCode: string | null
  submit: (args: SubmitTriageArgs) => Promise<void>
  handleRunOperationComplete: () => void
  reset: () => void
}

export function useContainerTriageSubmit(): UseContainerTriageSubmitResult {
  const [submitting, setSubmitting] = useState(false)
  const [submittedRun, setSubmittedRun] = useState<IngestionRunStart | null>(null)
  const [operationAcknowledged, setOperationAcknowledged] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)

  const submit = useCallback(async (args: SubmitTriageArgs) => {
    const { caseId, receiptId, selectedKeys, reingestKeys } = args
    setSubmitting(true)
    setError(null)
    setErrorCode(null)
    try {
      const run = await startIngestionRun(caseId, {
        collectionReceiptId: receiptId,
        selectedItemKeys: [...selectedKeys],
        reingestItemKeys: [...reingestKeys],
      })
      setSubmittedRun(run)
      setOperationAcknowledged(false)
    } catch (err) {
      const mapped = mapSubmitError(err)
      setError(mapped.message)
      setErrorCode(mapped.code)
    } finally {
      setSubmitting(false)
    }
  }, [])

  const handleRunOperationComplete = useCallback(() => {
    setOperationAcknowledged(true)
  }, [])

  const reset = useCallback(() => {
    setSubmittedRun(null)
    setOperationAcknowledged(false)
    setError(null)
    setErrorCode(null)
  }, [])

  const submittedOperationId =
    submittedRun?.operationId !== null && submittedRun?.operationId !== undefined && !operationAcknowledged
      ? submittedRun.operationId
      : null

  return {
    submitting,
    submittedRun,
    submittedOperationId,
    error,
    errorCode,
    submit,
    handleRunOperationComplete,
    reset,
  }
}
